/**
 * Build player-to-player settlement from payment history.
 * All amounts are in chips; multiply by creditValue for dollars.
 */

function ensurePair(map, fromId) {
  if (!map[fromId]) map[fromId] = {};
}

/**
 * @param {Array<{playerID: string, name: string}>} allPlayers
 * @param {Array<object>} paymentHistory
 */
function buildPairwiseOwes(allPlayers, paymentHistory) {
  const pairOwes = {};

  allPlayers.forEach((p) => ensurePair(pairOwes, p.playerID));

  (paymentHistory || []).forEach((payment) => {
    if (!payment || typeof payment !== "object") return;
    if (payment.type !== "win" && payment.type !== "tie") return;

    const contributions = payment.contributions || {};

    if (payment.type === "win") {
      const winnerID = payment.winner;
      if (!winnerID) return;
      allPlayers.forEach((p) => {
        const amt = contributions[p.playerID] || 0;
        if (p.playerID !== winnerID && amt > 0) {
          ensurePair(pairOwes, p.playerID);
          pairOwes[p.playerID][winnerID] = (pairOwes[p.playerID][winnerID] || 0) + amt;
        }
      });
    } else if (payment.type === "tie") {
      const tiedPlayers = payment.players || [];
      const k = tiedPlayers.length;
      if (k === 0) return;
      allPlayers.forEach((p) => {
        const amt = contributions[p.playerID] || 0;
        if (!tiedPlayers.includes(p.playerID) && amt > 0) {
          const share = amt / k;
          tiedPlayers.forEach((tid) => {
            ensurePair(pairOwes, p.playerID);
            pairOwes[p.playerID][tid] = (pairOwes[p.playerID][tid] || 0) + share;
          });
        }
      });
    }
  });

  return pairOwes;
}

function nameFor(allPlayers, id) {
  const p = allPlayers.find((x) => x.playerID === id);
  return p ? p.name : "Unknown";
}

/**
 * @param {Array<{playerID: string, name: string}>} allPlayers
 * @param {Array<object>} paymentHistory
 */
function buildPaymentSettlement(allPlayers, paymentHistory) {
  const pairOwes = buildPairwiseOwes(allPlayers, paymentHistory);

  const players = allPlayers.map((p) => {
    const id = p.playerID;
    const owesTo = {};
    const owedBy = {};
    let totalOwedToOthers = 0;
    let totalOwedToYou = 0;

    allPlayers.forEach((other) => {
      if (other.playerID === id) return;
      const youOwe = pairOwes[id]?.[other.playerID] || 0;
      const theyOwe = pairOwes[other.playerID]?.[id] || 0;
      if (youOwe > 0) {
        owesTo[other.playerID] = youOwe;
        totalOwedToOthers += youOwe;
      }
      if (theyOwe > 0) {
        owedBy[other.playerID] = theyOwe;
        totalOwedToYou += theyOwe;
      }
    });

    return {
      playerID: id,
      name: p.name,
      totalOwedToOthers,
      totalOwedToYou,
      owesTo,
      owedBy,
    };
  });

  const settlements = [];
  for (let i = 0; i < allPlayers.length; i++) {
    for (let j = i + 1; j < allPlayers.length; j++) {
      const a = allPlayers[i];
      const b = allPlayers[j];
      const aOwesB = pairOwes[a.playerID]?.[b.playerID] || 0;
      const bOwesA = pairOwes[b.playerID]?.[a.playerID] || 0;
      const net = aOwesB - bOwesA;
      if (net > 0.001) {
        settlements.push({
          from: a.playerID,
          fromName: a.name,
          to: b.playerID,
          toName: b.name,
          amount: net,
        });
      } else if (net < -0.001) {
        settlements.push({
          from: b.playerID,
          fromName: b.name,
          to: a.playerID,
          toName: a.name,
          amount: -net,
        });
      }
    }
  }

  settlements.sort((x, y) => y.amount - x.amount);

  return { players, settlements };
}

/**
 * Per-viewer summary for in-game payment tab.
 * @param {string} viewerPlayerID
 */
function buildViewerPaymentSummary(allPlayers, paymentHistory, viewerPlayerID) {
  const { players } = buildPaymentSettlement(allPlayers, paymentHistory);
  const viewer = players.find((p) => p.playerID === viewerPlayerID);
  if (!viewer) return [];

  return allPlayers
    .filter((p) => p.playerID !== viewerPlayerID)
    .map((other) => {
      const youOwe = viewer.owesTo[other.playerID] || 0;
      const theyOwe = viewer.owedBy[other.playerID] || 0;
      return {
        playerID: other.playerID,
        playerName: other.name,
        youOweThem: youOwe,
        theyOweYou: theyOwe,
      };
    })
    .filter((row) => row.youOweThem > 0 || row.theyOweYou > 0);
}

export { buildPaymentSettlement, buildViewerPaymentSummary };
